import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useModalState } from './modal.context';

const loadManagedModal = () => import('./managed-modal');

const ManagedModal = dynamic(loadManagedModal, { ssr: false });

/** Prefetch the managed-modal chunk (hover/focus/idle). */
export function preloadManagedModal() {
  void loadManagedModal();
}

/**
 * Mounts ManagedModal only after the first open (or preload), then keeps it
 * mounted so close animations still work without paying the cost on every page.
 */
export default function LazyManagedModal() {
  const { isOpen } = useModalState();
  const [shouldMount, setShouldMount] = useState(false);
  const mountedOnce = useRef(false);

  useEffect(() => {
    if (isOpen || mountedOnce.current) {
      mountedOnce.current = true;
      setShouldMount(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || shouldMount) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled) preloadManagedModal();
    };

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      timeoutId = setTimeout(run, 2500);
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [shouldMount]);

  if (!shouldMount && !isOpen) return null;
  return <ManagedModal />;
}
